import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useApi } from "../utils/api";
import { ENDPOINTS } from "../config/endpoints";

const slugify = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/ı/g, "i")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);

const emptyForm = { id: null, kod: "", ad: "", zorunlu: false, aktif: true, gorunur: true, sira: 0 };

const DosyaTurleriYonetim = () => {
  const http = useApi();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [kodTouched, setKodTouched] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await http.get(ENDPOINTS.DOSYA_TURU.LIST);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Dosya türleri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setKodTouched(false);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      kod: item.kod,
      ad: item.ad,
      zorunlu: !!item.zorunlu,
      aktif: !!item.aktif,
      gorunur: item.gorunur !== false,
      sira: item.sira ?? 0,
    });
    setKodTouched(true);
    setDialogOpen(true);
  };

  const handleAdChange = (val) => {
    setForm((s) => ({
      ...s,
      ad: val,
      kod: kodTouched ? s.kod : slugify(val),
    }));
  };

  const handleSave = async () => {
    if (!form.ad?.trim() || !form.kod?.trim()) {
      setError("Ad ve kod zorunlu.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        kod: form.kod.trim(),
        ad: form.ad.trim(),
        zorunlu: form.zorunlu,
        aktif: form.aktif,
        gorunur: form.gorunur,
        sira: Number(form.sira) || 0,
      };
      if (form.id) {
        await http.put(ENDPOINTS.DOSYA_TURU.DETAIL(form.id), payload);
        setSuccess("Dosya türü güncellendi.");
      } else {
        await http.post(ENDPOINTS.DOSYA_TURU.LIST, payload);
        setSuccess("Dosya türü eklendi.");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      setError(err.message || "Kayıt başarısız.");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    const t = deleteTarget;
    if (!t) return;
    setDeleting(true);
    setError("");
    try {
      await http.del(ENDPOINTS.DOSYA_TURU.DETAIL(t.id));
      setDeleteTarget(null);
      setSuccess(`"${t.ad}" silindi.`);
      fetchItems();
    } catch (err) {
      setError(err.message || "Silme başarısız.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Dosya Türleri
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Personel dosya yükleme alanında kullanılacak türleri buradan yönetebilirsiniz.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Yeni Tür
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#F9FAFB" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 60 }}>Sıra</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Ad</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kod</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 120 }}>Zorunlu</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>Aktif</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>Görünür</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 110 }} align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center"><CircularProgress size={18} /></TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 3 }}>
                  Henüz dosya türü eklenmemiş.
                </TableCell>
              </TableRow>
            )}
            {!loading && items.map((it) => (
              <TableRow key={it.id} hover>
                <TableCell>{it.sira}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{it.ad}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>{it.kod}</TableCell>
                <TableCell>
                  {it.zorunlu ? (
                    <Chip size="small" label="Zorunlu" color="warning" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Opsiyonel" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  {it.aktif ? (
                    <Chip size="small" label="Aktif" color="success" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Pasif" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  {it.gorunur ? (
                    <Chip size="small" label="Görünür" color="info" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Gizli" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Düzenle">
                    <IconButton size="small" onClick={() => openEdit(it)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(it)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Ekle/Düzenle Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? "Dosya Türünü Düzenle" : "Yeni Dosya Türü"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ad*"
              value={form.ad}
              onChange={(e) => handleAdChange(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              label="Kod*"
              value={form.kod}
              onChange={(e) => {
                setKodTouched(true);
                setForm((s) => ({ ...s, kod: e.target.value }));
              }}
              helperText="Sistem kodu (lowercase, _ ayraç). Ad'a göre otomatik üretilir."
              fullWidth
            />
            <TextField
              label="Sıra"
              type="number"
              value={form.sira}
              onChange={(e) => setForm((s) => ({ ...s, sira: e.target.value }))}
              sx={{ width: 180 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.zorunlu}
                  onChange={(e) => setForm((s) => ({ ...s, zorunlu: e.target.checked }))}
                />
              }
              label="Zorunlu (her personel için yüklenmesi beklenir)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.aktif}
                  onChange={(e) => setForm((s) => ({ ...s, aktif: e.target.checked }))}
                />
              }
              label="Aktif"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.gorunur}
                  onChange={(e) => setForm((s) => ({ ...s, gorunur: e.target.checked }))}
                />
              }
              label="Görünür (personel tablosunda var/yok rozetiyle gösterilir)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : (form.id ? "Güncelle" : "Kaydet")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Onay */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Dosya Türünü Sil</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            <strong>{deleteTarget?.ad}</strong> ({deleteTarget?.kod}) silinsin mi? Kullanımdaysa silinemez.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : "Sil"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DosyaTurleriYonetim;
